import React, { useState, useMemo } from 'react'
import { Pagination } from '@material-ui/lab'
import {
    useTheme,
    TableSortLabel,
    Tooltip,
    Typography,
    Chip,
} from '@material-ui/core'
import { PropertyGradeRow } from './PropertyGradeRow'
import { Container } from '../../../components'
import {
    ChartData,
    PropertyGradeResponseData,
} from '../../../hooks/useAnalyticData'
import { usePagination } from '../../../hooks'
import { Range } from 'react-date-range'
import { DateFilter } from '../../../components/DateFilterButtons'
import { useUserTags } from '../../../contexts/UserTagsContext'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    propertyGradeList: PropertyGradeResponseData[] | null
    timeline: Range
    dateFilter: DateFilter
    chartData: ChartData | null
    selectedPropertyGrade: PropertyGradeResponseData | null
    getChartData: (
        apartmentId: number,
        timeline?: { startDate?: Date; endDate?: Date },
    ) => void
    getInspectionTypeGrades: (
        apartmentId: number,
        timeline?: { startDate?: Date; endDate?: Date },
        filters?: {
            inspection_type_name?: string
            inspection_type_category?: string
        },
    ) => void
    getUnitInspectionCompletionData: (
        apartmentId: number,
        timeline?: { startDate?: Date; endDate?: Date },
    ) => Promise<any>
    setPropertyGradeList: (
        propertyGradeList: PropertyGradeResponseData[],
    ) => void
    setOpenInspectionTypeModal: (open: boolean) => void
    setSelectedPropertyGrade: (propertyGrade: PropertyGradeResponseData) => void
    selectedTagIds?: number[]
    inspectionTypeFilters?: {
        inspection_type_name?: string
        inspection_type_category?: string
    }
}

export const PropertyGradeTable = (props: Props) => {
    const {
        propertyGradeList,
        timeline,
        dateFilter,
        chartData,
        selectedPropertyGrade,
        setPropertyGradeList,
        setSelectedPropertyGrade,
        getChartData,
        getInspectionTypeGrades,
        getUnitInspectionCompletionData,
        setOpenInspectionTypeModal,
        selectedTagIds = [],
        inspectionTypeFilters,
    } = props
    const { userTags } = useUserTags()

    // Helper function to render tags
    const renderTags = (propertyGrade: PropertyGradeResponseData) => {
        const relevantTags = userTags.filter((tag) =>
            tag.apartments.includes(propertyGrade.apartment_id),
        )

        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    flexWrap: 'wrap',
                }}
            >
                {relevantTags.map((tag) => (
                    <Chip
                        key={tag.id}
                        label={tag.name}
                        size="small"
                        style={{
                            backgroundColor: tag.color,
                            color: 'white',
                            fontSize: '0.7rem',
                            height: '20px',
                        }}
                    />
                ))}
            </div>
        )
    }

    // Filter property grades by selected tags
    const filteredPropertyGradeList = useMemo(() => {
        if (!propertyGradeList || selectedTagIds.length === 0) {
            return propertyGradeList
        }

        return propertyGradeList.filter((propertyGrade) => {
            // Find tags that include this apartment
            const relevantTags = userTags.filter((tag) =>
                tag.apartments.includes(propertyGrade.apartment_id),
            )

            // Check if any of the relevant tags are in the selected tags
            return relevantTags.some((tag) => selectedTagIds.includes(tag.id))
        })
    }, [propertyGradeList, selectedTagIds, userTags])

    const { page, setPage, pageData, numPages, start, end } = usePagination(
        filteredPropertyGradeList ?? [],
    )
    const [orderBy, setOrderBy] = useState('')

    const theme = useTheme()
    const headCellStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.h6),
        fontWeight: 600,
        color: theme.palette.darkGreen.main,
        justifyContent: 'center',
        flex: 1,
    }

    return (
        <Container direction="column" style={{ marginTop: theme.spacing(3) }}>
            {/* HEADERS */}
            <Container
                style={{
                    padding: theme.spacing(0, 1),
                }}
            >
                <Container
                    style={{
                        ...headCellStyle,
                    }}
                    onClick={() => {
                        if (propertyGradeList) {
                            const newRows = [...propertyGradeList]
                            if (orderBy !== LocationASC) {
                                newRows.sort((RowA, RowB) =>
                                    RowA.name.localeCompare(RowB.name),
                                )
                                setPropertyGradeList(newRows)
                                setOrderBy(LocationASC)
                            } else {
                                setPropertyGradeList(newRows.reverse())
                                setOrderBy(LocationDESC)
                            }
                        }
                    }}
                >
                    <TableSortLabel
                        active={
                            orderBy === LocationASC || orderBy === LocationDESC
                        }
                        direction={orderBy === LocationASC ? 'asc' : 'desc'}
                    >
                        Location
                    </TableSortLabel>
                </Container>

                <Container
                    style={{
                        ...headCellStyle,
                        alignItems: 'center',
                    }}
                >
                    Tag
                </Container>

                <Container
                    style={{ ...headCellStyle }}
                    onClick={() => {
                        if (propertyGradeList) {
                            const newRows = [...propertyGradeList]
                            if (orderBy !== BipsASC) {
                                newRows.sort(
                                    (RowA, RowB) => RowA.bips - RowB.bips,
                                )
                                setPropertyGradeList(newRows)
                                setOrderBy(BipsASC)
                            } else {
                                setPropertyGradeList(newRows.reverse())
                                setOrderBy(BipsDESC)
                            }
                        }
                    }}
                >
                    <Tooltip title="Building and Inspector Performance Score">
                        <TableSortLabel
                            active={orderBy === BipsASC || orderBy === BipsDESC}
                            direction={orderBy === BipsASC ? 'asc' : 'desc'}
                        >
                            BIPS
                        </TableSortLabel>
                    </Tooltip>
                </Container>
                <Container
                    style={{
                        ...headCellStyle,
                    }}
                    onClick={() => {
                        if (propertyGradeList) {
                            const newRows = [...propertyGradeList]
                            if (orderBy !== BuildingASC) {
                                newRows.sort(
                                    (RowA, RowB) =>
                                        RowA.building_score -
                                        RowB.building_score,
                                )
                                setPropertyGradeList(newRows)
                                setOrderBy(BuildingASC)
                            } else {
                                setPropertyGradeList(newRows.reverse())
                                setOrderBy(BuildingDESC)
                            }
                        }
                    }}
                >
                    <TableSortLabel
                        active={
                            orderBy === BuildingASC || orderBy === BuildingDESC
                        }
                        direction={orderBy === BuildingASC ? 'asc' : 'desc'}
                    >
                        Building Health
                    </TableSortLabel>
                </Container>
                <Container
                    style={{
                        ...headCellStyle,
                    }}
                    onClick={() => {
                        if (propertyGradeList) {
                            const newRows = [...propertyGradeList]
                            if (orderBy !== InspectorASC) {
                                newRows.sort(
                                    (RowA, RowB) =>
                                        RowA.inspector_score -
                                        RowB.inspector_score,
                                )
                                setPropertyGradeList(newRows)
                                setOrderBy(InspectorASC)
                            } else {
                                setPropertyGradeList(newRows.reverse())
                                setOrderBy(InspectorDESC)
                            }
                        }
                    }}
                >
                    <TableSortLabel
                        active={
                            orderBy === InspectorASC ||
                            orderBy === InspectorDESC
                        }
                        direction={orderBy === InspectorASC ? 'asc' : 'desc'}
                    >
                        Inspector Performance
                    </TableSortLabel>
                </Container>
                <Container
                    style={{ ...headCellStyle }}
                    onClick={() => {
                        if (propertyGradeList) {
                            const newRows = [...propertyGradeList]
                            if (orderBy !== UnitsInspectedASC) {
                                newRows.sort(
                                    (RowA, RowB) =>
                                        RowA.units_inspected -
                                        RowB.units_inspected,
                                )
                                setPropertyGradeList(newRows)
                                setOrderBy(UnitsInspectedASC)
                            } else {
                                setPropertyGradeList(newRows.reverse())
                                setOrderBy(UnitsInspectedDESC)
                            }
                        }
                    }}
                >
                    <Tooltip title="Number of units that have been inspected">
                        <TableSortLabel
                            active={
                                orderBy === UnitsInspectedASC ||
                                orderBy === UnitsInspectedDESC
                            }
                            direction={
                                orderBy === UnitsInspectedASC ? 'asc' : 'desc'
                            }
                        >
                            Units Inspected
                        </TableSortLabel>
                    </Tooltip>
                </Container>
                <Container
                    style={{ ...headCellStyle }}
                    onClick={() => {
                        if (propertyGradeList) {
                            const newRows = [...propertyGradeList]
                            if (orderBy !== UnitsRequiringASC) {
                                newRows.sort(
                                    (RowA, RowB) =>
                                        RowA.units_requiring_inspection -
                                        RowB.units_requiring_inspection,
                                )
                                setPropertyGradeList(newRows)
                                setOrderBy(UnitsRequiringASC)
                            } else {
                                setPropertyGradeList(newRows.reverse())
                                setOrderBy(UnitsRequiringDESC)
                            }
                        }
                    }}
                >
                    <Tooltip title="Number of units that require inspection">
                        <TableSortLabel
                            active={
                                orderBy === UnitsRequiringASC ||
                                orderBy === UnitsRequiringDESC
                            }
                            direction={
                                orderBy === UnitsRequiringASC ? 'asc' : 'desc'
                            }
                        >
                            Units Requiring
                        </TableSortLabel>
                    </Tooltip>
                </Container>
                <Container
                    style={{ ...headCellStyle }}
                    onClick={() => {
                        if (propertyGradeList) {
                            const newRows = [...propertyGradeList]
                            if (orderBy !== InspectionPctASC) {
                                newRows.sort((RowA, RowB) => {
                                    const pctA =
                                        RowA.units_requiring_inspection > 0
                                            ? RowA.units_inspected /
                                              RowA.units_requiring_inspection
                                            : 0
                                    const pctB =
                                        RowB.units_requiring_inspection > 0
                                            ? RowB.units_inspected /
                                              RowB.units_requiring_inspection
                                            : 0
                                    return pctA - pctB
                                })
                                setPropertyGradeList(newRows)
                                setOrderBy(InspectionPctASC)
                            } else {
                                setPropertyGradeList(newRows.reverse())
                                setOrderBy(InspectionPctDESC)
                            }
                        }
                    }}
                >
                    <Tooltip title="Percentage of required units that have been inspected">
                        <TableSortLabel
                            active={
                                orderBy === InspectionPctASC ||
                                orderBy === InspectionPctDESC
                            }
                            direction={
                                orderBy === InspectionPctASC ? 'asc' : 'desc'
                            }
                        >
                            Inspection %
                        </TableSortLabel>
                    </Tooltip>
                </Container>
                <Container
                    style={{ ...headCellStyle }}
                    onClick={() => {
                        if (propertyGradeList) {
                            const newRows = [...propertyGradeList]
                            if (orderBy !== LastUnitCompletedDateASC) {
                                newRows.sort((RowA, RowB) => {
                                    if (
                                        !RowA.last_unit_completed_date &&
                                        !RowB.last_unit_completed_date
                                    )
                                        return 0
                                    if (!RowA.last_unit_completed_date)
                                        return -1
                                    if (!RowB.last_unit_completed_date) return 1

                                    const dateA = new Date(
                                        RowA.last_unit_completed_date,
                                    )
                                    const dateB = new Date(
                                        RowB.last_unit_completed_date,
                                    )

                                    return dateA.getTime() - dateB.getTime()
                                })
                                setPropertyGradeList(newRows)
                                setOrderBy(LastUnitCompletedDateASC)
                            } else {
                                setPropertyGradeList(newRows.reverse())
                                setOrderBy(LastUnitCompletedDateDESC)
                            }
                        }
                    }}
                >
                    <TableSortLabel
                        active={
                            orderBy === LastUnitCompletedDateASC ||
                            orderBy === LastUnitCompletedDateDESC
                        }
                        direction={
                            orderBy === LastUnitCompletedDateASC
                                ? 'asc'
                                : 'desc'
                        }
                    >
                        Last Unit Completed Date
                    </TableSortLabel>
                </Container>
                <Container
                    style={{ ...headCellStyle }}
                    onClick={() => {
                        if (propertyGradeList) {
                            const newRows = [...propertyGradeList]
                            if (orderBy !== TrendASC) {
                                newRows.sort(
                                    (RowA, RowB) =>
                                        RowA.trend_value - RowB.trend_value,
                                )
                                setPropertyGradeList(newRows)
                                setOrderBy(TrendASC)
                            } else {
                                setPropertyGradeList(newRows.reverse())
                                setOrderBy(TrendDESC)
                            }
                        }
                    }}
                >
                    <TableSortLabel
                        active={orderBy === TrendASC || orderBy === TrendDESC}
                        direction={orderBy === TrendASC ? 'asc' : 'desc'}
                    >
                        Trend
                    </TableSortLabel>
                </Container>
            </Container>
            {/* body */}
            <Container
                style={{
                    overflowY: 'auto',
                    maxHeight: 'calc(100vh - 350px)',
                    minHeight: 'calc(100vh - 350px)',
                }}
                direction="column"
            >
                {pageData.length > 0 ? (
                    pageData.map((propertyGrade, idx) => {
                        return (
                            <PropertyGradeRow
                                propertyGrade={propertyGrade}
                                chartData={chartData}
                                getChartData={getChartData}
                                getInspectionTypeGrades={
                                    getInspectionTypeGrades
                                }
                                inspectionTypeFilters={inspectionTypeFilters}
                                getUnitInspectionCompletionData={
                                    getUnitInspectionCompletionData
                                }
                                timeline={timeline}
                                dateFilter={dateFilter}
                                setOpenInspectionTypeModal={
                                    setOpenInspectionTypeModal
                                }
                                setSelectedPropertyGrade={
                                    setSelectedPropertyGrade
                                }
                                isSelected={
                                    propertyGrade.id ===
                                    selectedPropertyGrade?.id
                                }
                                renderTags={renderTags}
                                key={`PROPERTY_GRADE_${propertyGrade.id}`}
                            />
                        )
                    })
                ) : (
                    <Container>
                        <Typography>No data found</Typography>
                    </Container>
                )}
            </Container>
            {/* footer */}
            <Container
                style={{
                    ...typographyStyle(theme.typography.subtitle1),
                    borderTop: `1px solid ${theme.palette.grey[400]}`,
                    padding: theme.spacing(1),
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Pagination
                    count={numPages}
                    page={page}
                    onChange={(_: unknown, newPage: number) => {
                        setPage(newPage)
                    }}
                />
                <span>
                    {start} - {end} of {filteredPropertyGradeList?.length ?? 0}
                </span>
            </Container>
        </Container>
    )
}

const LocationASC = 'LocationASC'
const LocationDESC = 'LocationDESC'
const BuildingASC = 'BuildingASC'
const BuildingDESC = 'BuildingDESC'
const InspectorASC = 'InspectorASC'
const InspectorDESC = 'InspectorDESC'
const BipsASC = 'BipsASC'
const BipsDESC = 'BipsDESC'
const TrendASC = 'TrendASC'
const TrendDESC = 'TrendDESC'
const LastUnitCompletedDateASC = 'LastUnitCompletedDateASC'
const LastUnitCompletedDateDESC = 'LastUnitCompletedDateDESC'
const UnitsInspectedASC = 'UnitsInspectedASC'
const UnitsInspectedDESC = 'UnitsInspectedDESC'
const UnitsRequiringASC = 'UnitsRequiringASC'
const UnitsRequiringDESC = 'UnitsRequiringDESC'
const InspectionPctASC = 'InspectionPctASC'
const InspectionPctDESC = 'InspectionPctDESC'
